/**
 * Cliente del «significado de esa frase» (encargo 49):
 * `GET /lexicon/meaning/config` y `POST /lexicon/meaning`. Aparte de
 * `lexiconApi.ts` para que prenderlo no toque a quien sólo lee el catálogo.
 */
import { createTypedApiClient } from '@orborix/auth-client';
import type {
  ContextMeaningPublicConfig,
  ContextMeaningRequest,
  ContextMeaningResult,
} from '@orborix/shared-types';
import { lexiconWordFromDto, type LexiconEntryDto, type LexiconWord } from './lexiconApi.js';

const client = createTypedApiClient({
  basePath: '/api/modules/english',
  errorName: 'ContextMeaningApiError',
  abortError: 'wrap',
});

export const ContextMeaningApiError = client.ApiError;

async function jsonFetch<T>(path: string): Promise<T> {
  const res = await client.request(path);
  await client.ensureOk(res);
  return (await res.json()) as T;
}

/**
 * Lo que vuelve de `POST /lexicon/meaning`, con la entrada ya en la forma de la
 * Biblioteca (para abrir la ficha sin otro pedido). El contrato vive en
 * `@orborix/shared-types` (`context-meaning.ts`).
 */
export type ContextMeaning = ContextMeaningResult<LexiconWord>;

/**
 * Si está prendido (una vez por carga): apagado, tocar una palabra hace lo de
 * hoy y ni se pregunta por el significado. Un error de red cuenta como
 * apagado — el toque de hoy sigue andando —, pero se recuerda sólo hasta la
 * próxima carga.
 */
let meaningConfig: Promise<ContextMeaningPublicConfig> | null = null;
export function fetchContextMeaningConfig(): Promise<ContextMeaningPublicConfig> {
  meaningConfig ??= jsonFetch<{ data?: ContextMeaningPublicConfig }>('/lexicon/meaning/config')
    .then((r) => {
      if (!r.data || typeof r.data.enabled !== 'boolean')
        throw new ContextMeaningApiError(
          'Respuesta de /lexicon/meaning/config sin forma',
          500,
          'bad_meaning_config',
        );
      return r.data;
    })
    .catch((err: unknown) => {
      console.warn('[context-meaning] config no disponible: el toque queda como hoy', err);
      return { enabled: false, missingWordDisplay: 'notice-only' } as const;
    });
  return meaningConfig;
}

/** Sólo tests: olvidar la config leída. */
export function resetContextMeaningConfigForTests(): void {
  meaningConfig = null;
}

/** Pide el significado de la palabra tocada en su oración. Un error TIRA (el que llama decide qué mostrar). */
export async function fetchContextMeaning(req: ContextMeaningRequest): Promise<ContextMeaning> {
  const res = await client.request('/lexicon/meaning', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(req),
  });
  await client.ensureOk(res);
  const json = (await res.json()) as { data?: ContextMeaningResult<LexiconEntryDto> };
  const d = json.data;
  if (!d || typeof d.status !== 'string')
    throw new ContextMeaningApiError(
      'Respuesta de /lexicon/meaning sin forma',
      500,
      'bad_meaning_payload',
    );
  switch (d.status) {
    case 'picked':
    case 'unchosen':
      return { ...d, entry: lexiconWordFromDto(d.entry) };
    default:
      return d;
  }
}
